import { Filter, TrendingUp, Users, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { useAuthStore } from '../store/auth';

interface AnalyticsEvent {
  id: string;
  agency_id: string;
  user_id: string;
  event_type: string;
  event_name: string;
  page_path: string;
  created_at: string;
}

interface TopAgencyStats {
  id: string;
  name: string;
  event_count: number;
}

interface AnalyticsSummary {
  total_events: number;
  total_users: number;
  total_agencies: number;
  events_last_7_days: number;
  events_by_type: Record<string, number>;
  top_agencies: TopAgencyStats[];
}

export default function SuperAdminAnalytics() {
  const token = useAuthStore((s) => s.accessToken);
  const [events, setEvents] = useState<AnalyticsEvent[]>([]);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    agency_id: '',
    event_type: '',
    page: 1,
  });
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const limit = 50;

  useEffect(() => {
    fetchAnalytics();
  }, [filter]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      // Fetch summary
      const summaryResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/superadmin/analytics/summary`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json();
        setSummary(summaryData);
      }

      // Fetch events
      const params = new URLSearchParams({
        page: filter.page.toString(),
        limit: limit.toString(),
      });

      if (filter.agency_id) {
        params.append('agency_id', filter.agency_id);
      }
      if (filter.event_type) {
        params.append('event_type', filter.event_type);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/superadmin/analytics/events?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch events');

      const data = await response.json();
      setEvents(data.data || []);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const eventTypes = summary?.events_by_type
    ? Object.keys(summary.events_by_type)
    : [];

  return (
    <SuperAdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Analytics</h2>
          <p className="mt-2 text-gray-600">Track all user events across agencies</p>
        </div>

        {/* Stats Overview */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Events</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900">
                    {summary.total_events.toLocaleString()}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-blue-600 opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Users</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900">
                    {summary.total_users.toLocaleString()}
                  </p>
                </div>
                <Users className="h-8 w-8 text-green-600 opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Agencies</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900">
                    {summary.total_agencies}
                  </p>
                </div>
                <Zap className="h-8 w-8 text-purple-600 opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Last 7 Days</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900">
                    {summary.events_last_7_days.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Event Types Chart */}
        {summary && Object.keys(summary.events_by_type).length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Events by Type
            </h3>
            <div className="space-y-3">
              {Object.entries(summary.events_by_type).map(([type, count]) => {
                const total = Object.values(summary.events_by_type).reduce(
                  (a, b) => a + b,
                  0
                );
                const percentage = ((count / total) * 100).toFixed(1);
                return (
                  <div key={type} className="flex items-center gap-4">
                    <div className="w-32 font-medium text-gray-900">
                      {type}
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="w-20 text-right text-gray-600">
                      {count.toLocaleString()}
                      <span className="text-xs ml-1">({percentage}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Top Agencies */}
        {summary && summary.top_agencies.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Top Agencies by Events
            </h3>
            <div className="space-y-2">
              {summary.top_agencies.map((agency: TopAgencyStats, idx: number) => (
                <div
                  key={agency.id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-gray-600 w-6 text-center">
                      {idx + 1}
                    </span>
                    <span className="font-medium text-gray-900">
                      {agency.name}
                    </span>
                  </div>
                  <span className="font-semibold text-blue-600">
                    {agency.event_count.toLocaleString()} events
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filter Events
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Event Type
              </label>
              <select
                value={filter.event_type}
                onChange={(e) =>
                  setFilter({ ...filter, event_type: e.target.value, page: 1 })
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                {eventTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Agency ID
              </label>
              <input
                type="text"
                placeholder="Filter by agency ID"
                value={filter.agency_id}
                onChange={(e) =>
                  setFilter({ ...filter, agency_id: e.target.value, page: 1 })
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Events Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <p className="mt-2">Loading events...</p>
            </div>
          ) : events.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No events found
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-gray-200 bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900">
                        Event Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900">
                        Event Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900">
                        Page Path
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900">
                        Agency
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900">
                        Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {events.map((event) => (
                      <tr
                        key={event.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                            {event.event_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-medium text-gray-900">
                          {event.event_name}
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {event.page_path}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {event.agency_id.substring(0, 8)}...
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(event.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing {(filter.page - 1) * limit + 1} to{' '}
                  {Math.min(filter.page * limit, total)} of {total}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      setFilter({
                        ...filter,
                        page: Math.max(1, filter.page - 1),
                      })
                    }
                    disabled={filter.page === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                  >
                    Previous
                  </button>

                  <div className="flex items-center gap-1">
                    {filter.page > 2 && (
                      <button
                        onClick={() =>
                          setFilter({ ...filter, page: 1 })
                        }
                        className="px-3 py-1 border border-gray-300 rounded text-gray-700"
                      >
                        1
                      </button>
                    )}
                    {filter.page > 3 && (
                      <span className="px-2">...</span>
                    )}
                    {Array.from({
                      length: Math.min(
                        3,
                        Math.max(
                          1,
                          totalPages - Math.ceil(filter.page / 2)
                        )
                      ),
                    }).map((_, i) => {
                      const page = Math.max(1, filter.page - 1 + i);
                      if (page >= totalPages - 1) return null;
                      return (
                        <button
                          key={page}
                          onClick={() =>
                            setFilter({ ...filter, page })
                          }
                          className={`px-3 py-1 rounded ${
                            page === filter.page
                              ? 'bg-blue-600 text-white'
                              : 'border border-gray-300 text-gray-700'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    {filter.page < totalPages - 2 && (
                      <span className="px-2">...</span>
                    )}
                    {filter.page < totalPages - 1 && (
                      <button
                        onClick={() =>
                          setFilter({ ...filter, page: totalPages })
                        }
                        className="px-3 py-1 border border-gray-300 rounded text-gray-700"
                      >
                        {totalPages}
                      </button>
                    )}
                  </div>

                  <button
                    onClick={() =>
                      setFilter({
                        ...filter,
                        page: Math.min(totalPages, filter.page + 1),
                      })
                    }
                    disabled={filter.page === totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </SuperAdminLayout>
  );
}
